import React, { useState } from 'react';
import PropTypes from 'prop-types';
import s from './ModalFileSubmissionForm.module.scss';
import Modal from '../Modal/Modal';
import classNames from 'classnames';
import Icon from '../Icon';
import Button from '../Button';
import FormTextAreaField from '../FormTextAreaField/FormTextAreaField';
import { useForm } from 'react-hook-form';
import { t } from '../../i18n';
import FormDropZone from '../FormDropZone/FormDropZone';

const ModalFileSubmissionForm = ({
    dashboardId,
    isCustomButton = false,
    buttonContent,
}) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState([]);

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
    };

    const handleFormSubmit = async () => {
        setLoading(true);
        try {
            const formPayload = new FormData();

            formPayload.append('text', description);
            formPayload.append('dashboard', dashboardId);

            files.forEach((file) => {
                if (file.originalFile) {
                    formPayload.append(`files[]`, file.originalFile);
                }
            });

            const csrfToken = getCookie('csrftoken');

            const response = await fetch('/wt/submit-files/', {
                method: 'POST',
                headers: {
                    'X-CSRFToken': csrfToken,
                },
                body: formPayload,
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSuccess(true);
            } else {
                console.error('Submission failed:', data.errors);
            }
        } catch (error) {
            console.error('Error sending files:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setLoading(false);
        setSuccess(false);
        setDescription('');
        setFiles([]);
        reset();
    };

    const contentClasses = classNames(s.ModalContent, {
        [s['ModalContent--Success']]: success,
    });

    const footerClasses = classNames(s.Footer, {
        [s['Footer--Centered']]: loading || success,
    });

    return (
        <Modal
            onClose={handleCloseModal}
            disableOutsideClick
            title={t('modalFileSubmissionForm.title')}
            buttonContent={buttonContent}
            isCustomButton={isCustomButton}
            customButtonModifier="Primary"
            showButtonArrow={true}
            modifiers={['Sidebar']}
            buttonTitle={t('modalFileSubmissionForm.submitNewRequest')}>
            {({ close }) => (
                <div className={s.ModalWrapper}>
                    <div className={contentClasses}>
                        {success ? (
                            <div className={s.SuccessMessage}>
                                <Icon
                                    name="CheckGreenCircleFilled"
                                    className={s.SuccessIcon}
                                />
                                <p>
                                    {t(
                                        'modalFileSubmissionForm.successMessage'
                                    )}
                                </p>
                            </div>
                        ) : (
                            <>
                                <FormTextAreaField
                                    rows={5}
                                    placeholder={t(
                                        'modalFileSubmissionForm.descriptionField.placeholder'
                                    )}
                                    label={t(
                                        'modalFileSubmissionForm.descriptionField.label'
                                    )}
                                    name="description"
                                    errors={errors}
                                    register={{
                                        ...register('description', {
                                            value: description,
                                            onChange: (e) =>
                                                setDescription(e.target.value),
                                        }),
                                    }}
                                />

                                <FormDropZone
                                    label={t(
                                        'modalFileSubmissionForm.uploadFiles'
                                    )}
                                    files={files}
                                    setFiles={setFiles}
                                />
                            </>
                        )}
                    </div>

                    <div className={footerClasses}>
                        {!loading && !success && (
                            <>
                                <button
                                    type="button"
                                    className={s.CancelButton}
                                    onClick={close}>
                                    {t('modalFileSubmissionForm.cancel')}
                                </button>
                                <Button
                                    modifier="Secondary"
                                    size="Small"
                                    title={t('modalFileSubmissionForm.submit')}
                                    disabled={!files.length}
                                    onClick={handleSubmit(handleFormSubmit)}
                                />
                            </>
                        )}
                        {loading && (
                            <div className={s.StatusText}>
                                <div className={s.Spinner}>
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        )}
                        {!loading && success && (
                            <Button
                                modifier="Secondary"
                                size="Small"
                                title={t('modalFileSubmissionForm.close')}
                                onClick={close}
                            />
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
};

ModalFileSubmissionForm.propTypes = {
    dashboardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isCustomButton: PropTypes.bool,
    buttonContent: PropTypes.node,
};

export default ModalFileSubmissionForm;
